import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Every filter on the page lives in the URL, so a view is shareable, survives
 * a refresh, and a click on one tab (a workspace, a person, a day on a chart)
 * fills the filter every other tab reads.
 *
 *   tab · range (preset id or `custom`) · from / to (YYYY-MM-DD, UTC, custom only)
 *   env · ws (workspace) · person · status (a run-status group, conversations)
 *   compare (previous-period deltas) · session (the open conversation)
 *   c.* — the conversation list's own filters
 *
 * Days are UTC because the agent buckets its daily series in UTC: a bar
 * clicked on the chart and the range it filters to are then the same day.
 */

const DAY = 86_400_000;

export const PRESETS = [
  { id: "24h", label: "Last 24 hours", short: "24h", days: 1 },
  { id: "7d", label: "Last 7 days", short: "7D", days: 7 },
  { id: "30d", label: "Last 30 days", short: "30D", days: 30 },
  { id: "90d", label: "Last 90 days", short: "90D", days: 90 },
  { id: "mtd", label: "Month to date", short: "MTD" },
  { id: "ytd", label: "Year to date", short: "YTD" },
];

export const DEFAULTS = { tab: "overview", range: "30d", env: "production", compare: "1" };

export const toDay = (t) => new Date(t).toISOString().slice(0, 10);
export const dayStart = (d) => Date.parse(`${d}T00:00:00.000Z`);

/** The window a filter state means: from/to instants, and the equally long window just before it. */
export function windowOf(f, now = Date.now()) {
  let from;
  let to = now;
  if (f.range === "custom" && f.from) {
    from = dayStart(f.from);
    to = Math.min(now, dayStart(f.to || f.from) + DAY);
  } else if (f.range === "mtd") {
    const d = new Date(now);
    from = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
  } else if (f.range === "ytd") {
    from = Date.UTC(new Date(now).getUTCFullYear(), 0, 1);
  } else {
    from = now - (PRESETS.find((p) => p.id === f.range)?.days ?? 30) * DAY;
  }
  const span = Math.max(DAY / 24, to - from);
  return { from, to, prevFrom: from - span, prevTo: from, days: Math.max(1, Math.round(span / DAY)) };
}

export function rangeLabel(f) {
  if (f.range !== "custom") return PRESETS.find((p) => p.id === f.range)?.label ?? "Last 30 days";
  const opts = { day: "numeric", month: "short", timeZone: "UTC" };
  const a = new Date(dayStart(f.from)).toLocaleDateString(undefined, opts);
  if (!f.to || f.to === f.from) return new Date(dayStart(f.from)).toLocaleDateString(undefined, { ...opts, weekday: "short", year: "numeric" });
  const b = new Date(dayStart(f.to)).toLocaleDateString(undefined, { ...opts, year: "numeric" });
  return `${a} – ${b}`;
}

const Ctx = createContext(null);

export function FiltersProvider({ children }) {
  const [search, setSearch] = useSearchParams();
  const [nonce, setNonce] = useState(0);

  const f = useMemo(() => {
    const out = { ...DEFAULTS };
    for (const [k, v] of search.entries()) out[k] = v;
    return out;
  }, [search]);

  /** Merge a patch into the URL; `null` / "" removes a key, and a default value is never written. */
  const set = useCallback(
    (patch) => {
      const unchanged = Object.entries(patch).every(([k, v]) => (search.get(k) ?? "") === (v == null || DEFAULTS[k] === v ? "" : String(v)));
      if (unchanged) return;
      setSearch(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (v == null || v === "" || DEFAULTS[k] === v) next.delete(k);
            else next.set(k, String(v));
          }
          return next;
        },
        { replace: false },
      );
    },
    [search, setSearch],
  );

  // The window is fixed when the range changes (or on refresh), not on every
  // filter change: opening a conversation must not re-run every query.
  const w = useMemo(() => windowOf(f), [f.range, f.from, f.to, nonce]);
  const [params, prevParams] = useMemo(() => {
    const base = { environment: f.env, ...(f.ws ? { accountId: f.ws } : {}), nonce };
    return [
      { from: new Date(w.from).toISOString(), to: new Date(w.to).toISOString(), ...base },
      { from: new Date(w.prevFrom).toISOString(), to: new Date(w.prevTo).toISOString(), ...base },
    ];
  }, [w, f.env, f.ws, nonce]);

  const value = useMemo(
    () => ({
      f,
      set,
      window: w,
      params,
      prevParams,
      compare: f.compare !== "0",
      refresh: () => setNonce((n) => n + 1),
      /** Conversation-list filters (the `c.` keys), as a plain object. */
      conv: Object.fromEntries(Object.entries(f).filter(([k]) => k.startsWith("c.")).map(([k, v]) => [k.slice(2), v])),
      setConv: (patch) => set(Object.fromEntries(Object.entries(patch).map(([k, v]) => [`c.${k}`, v]))),
    }),
    [f, set, w, params, prevParams],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useFilters = () => useContext(Ctx);

/** Filter to one UTC day (from a chart). */
export const dayFilter = (dayKey) => ({ range: "custom", from: dayKey, to: dayKey });
